import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    CheckCircle2,
    ChevronDown,
    BookOpen,
    Copy,
    ImagePlus,
    Check,
    ExternalLink,
    Info,
    ShieldAlert,
    AlertCircle,
    LogOut, Layers, ZoomIn, Image as ImageIcon, X, ChevronLeft,
    CreditCard, Loader2, Gift, Receipt, Lock
} from 'lucide-react';
import {
    channelAuthorization,
    kakaoProfileChannelAuth,
    logout,
    postSignupChannel, postSignupSubmit,
    getSignupRequest, getSignupQuote, postSignupPlacement, postSignupPay,
} from "../../api/account/account.api";
import {checkChannel} from "../../api/channel/channel.api";
import {fileUpload} from "../../api/file/file.api";
import {HOSTING_MALL_PLATFORMS, HOSTING_OMOPLAY} from "../../constants/hosting";
import useBackGuard from "../../libs/useBackGuard";
import {
    STICKER_COLORS, STICKER_ORIENTATIONS, STICKER_FULFILLMENT, QUANTITY_OPTIONS,
    stickerTypeId, parseStickerType, stickerImageUrl, describeStickerType,
    normalizeExtraQuantity, stickerNetOf,
} from "../../libs/stickerCatalog";
import {requestCardBillingAuth} from "../form/CardRegistrationPopup";
import Sticker1 from '../../assets/images/wizard/sticker_1.png'
import Sticker3 from '../../assets/images/wizard/sticker_3.png'
import IntroOverview from '../../assets/images/wizard/service_overview.png'
import BenefitRoulette from '../../assets/images/wizard/example_roulette.png'
import BenefitSetting from '../../assets/images/wizard/example_setting.png'

/**
 * 고객 최초 세팅(가입) 위저드 — 2026-10-12 개편.
 *
 * ## 단계 (사용자 명세 그대로)
 *
 * | STEP | 화면 | 서버 |
 * |---|---|---|
 * | INTRO | (2) 오모플레이 설명 통이미지 | — |
 * | LINK | (3) 오모플레이 / 다른 자사몰 + 사업자번호 | `POST /account/install-request/channel` (오모플레이 경로) |
 * | MALL_* | 자사몰 경로만: 채널 정보 → 관리자 인증 → 초대 ×2 | 인증 완료 시 위와 같은 API |
 * | PLACEMENT | (4) 스티커 색상·가로/세로·수량(이미지만/20장/추가) | `POST /account/install-request/placement` ← 🔑 토스로 떠나기 전에 저장 |
 * | (토스) | (5) 결제수단 등록. 카드가 이미 있으면 건너뜀 | `/payments/toss/billing/wizard` → `/wizard?step=payment` 복귀 |
 * | PAYMENT | (6) 신청내용 확인 → 결제하기 / 나중에 | `GET …/quote`, `POST …/pay` |
 * | BENEFIT_EXAMPLE | (7) 혜택 예시 | — |
 * | STORE | (8) 객단가·로고·컨셉 | `POST /account/install-request/submit` |
 * | DONE | 완료 | — |
 *
 * ## 🔴 두 가지를 지킬 것
 *
 * 1. **금액을 이 파일에 적지 않는다.** 서버 `InstallPricing` 이 소유하고 `GET /account/install-request/quote` 의
 *    `pricing` 으로 받는다. 숫자를 박으면 가격이 바뀔 때 화면과 청구가 어긋난다.
 * 2. **토스 카드 등록은 전체 페이지 이동이다.** 돌아오면 React 상태가 없다. 그래서 PLACEMENT 가 먼저 서버에
 *    저장하고, `?step=payment` 로 돌아온 위저드는 `getSignupRequest` 로 신청을 읽어 PAYMENT 에서 이어간다.
 *
 * 설계: `_ai-runs/2026-10-12_001_signup_wizard_setup_fee_payment/001_analysis_and_design.md`
 * 이전 개편: `_ai-runs/2026-09-10_001_signup_install_request_migration/`(신청을 `channel_install_request` 로)
 */
export const STEP = {
    INTRO: 0,
    LINK: 1,
    MALL_CHANNEL: 2,
    MALL_AUTH: 3,
    MALL_INVITE_BIZ: 4,
    MALL_INVITE_DEV: 5,
    PLACEMENT: 6,
    PAYMENT: 7,
    BENEFIT_EXAMPLE: 8,
    STORE: 9,
    DONE: 10,
};

/*
  연동 방식을 **명시적으로** 서버에 보낸다(2026-09-10).

  🔴 종전에는 이 의사표시를 저장할 칸이 없어 서버가 `hosting_name` 으로 유추했고,
     드롭다운 기본값 때문에 **오모플레이 경로 고객의 채널에 '카페24' 가 찍혔다**
     (2026-09-01, OB-25). 이제 `channel_install_request.link_type` 에 그대로 담긴다.
  ⚠️ 값은 서버 `ChannelInstallRequest.LinkType` 과 **문자열이 같아야** 한다.
*/
const LINK_TYPE_OMOPLAY = 'OMOPLAY';
const LINK_TYPE_MALL = 'MALL';

/** 토스 카드 등록 후 돌아올 곳. `App.js` 의 `/payments/toss/billing/wizard` 라우트가 `/wizard?step=payment` 로 보낸다. */
const CARD_REGISTRATION_RETURN_PATH = '/payments/toss/billing/wizard';

/**
 * (4) 에서 카드 등록창을 **이 횟수만큼 닫으면** '카드 없이 계속' 버튼이 나타난다(2026-10-12 사용자 결정).
 *
 * 처음부터 건너뛰기 버튼을 보이면 카드 등록을 미루는 고객이 늘어나고(설치비 미결제 = 세팅 지연),
 * 아예 없으면 카드가 준비되지 않은 고객이 (4) 에서 막힌다. 두 번 닫은 고객은 "지금은 안 되는" 고객이다.
 *
 * ⚠️ 데스크톱에서만 센다 — 토스 창이 **창으로 떠서** 닫힘이 Promise 거부(`USER_CANCEL`)로 돌아온다.
 *    모바일은 토스가 페이지 자체를 이동시키고, 실패·취소 복귀 주소가 (6) 이라 거기의 '나중에 등록하기' 가 같은 역할을 한다.
 */
const CARD_SKIP_AFTER_CANCELS = 2;

const won = (n) => `${Number(n ?? 0).toLocaleString()}원`;

/** 서버 결제 결과 코드(`InstallPaymentService.Result`). 바뀌면 서버도 바꿔야 한다. */
const PAY_RESULT = {
    SUCCESS: 'success',
    ALREADY_PAID: 'already_paid',
    NO_CARD: 'no_card',
    IN_PROGRESS: 'in_progress',
};

export default function ChannelConnectWizard() {
    const [step, setStep] = useState(STEP.INTRO);
    const [isAnimating, setIsAnimating] = useState(false);

    // 실제 온보딩은 대부분 '오모플레이 제공' 경로로 진행된다. 기본값을 그쪽으로 둔다(2026-08-20).
    const [linkType, setLinkType] = useState('omoplay');

    // LINK State
    const [channelName, setChannelName] = useState('');
    const [bizRegNumber, setBizRegNumber] = useState('');
    const [searchId, setSearchId] = useState('');
    const [validDuplicateChannel, setValidDuplicateChannel] = useState(false);

    // MALL_AUTH State
    const [phoneNumber, setPhoneNumber] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [isCodeSent, setIsCodeSent] = useState(false);
    const [isVerified, setIsVerified] = useState(false);
    /*
      ⛔ `channelId` 상태는 없다(2026-09-10). 신청은 채널을 만들지 않고, 최종 제출도 채널 id 를 받지 않는다
         (인증된 계정으로 신청이 결정된다 — 계정당 1건). 쓰이지 않는 식별자를 남기면 다음 사람이 채널 id 로 오해한다.
    */

    /*
      쇼핑몰 플랫폼 — 🔴 **기본값은 `HOSTING_OMOPLAY` 다. `'카페24'` 로 두면 안 된다**(2026-09-01, OB-25).
      오모플레이 경로는 LINK 에서 PLACEMENT 로 건너뛰어 고객이 이 UI 를 보지 못한 채 기본값이 서버로 갔다.
    */
    const [platform, setPlatform] = useState(HOSTING_OMOPLAY);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [mallId, setMallId] = useState('');
    const [mallPassword, setMallPassword] = useState('');
    const dropdownRef = useRef(null);

    // STORE State — 로고(선택) / 객단가(필수) / 매장 컨셉(선택)
    const [logoImage, setLogoImage] = useState(null);
    const fileInputRef = useRef(null);
    const [unitPrice, setUnitPrice] = useState(10000)
    // 빈 문자열로 시작한다. undefined 면 input 이 uncontrolled 로 잡혀 React 경고가 난다.
    const [concept, setConcept] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    /*
      PLACEMENT State — 스티커 하나뿐이다(2026-10-12). POP 은 선택지에서 빠졌다.

      값 = 색상 × 방향 → `sticker_type`(`hor_blue` 등), 수량 옵션 → `sticker_fulfillment` + `sticker_quantity`.
        · 이미지만  → IMAGE_ONLY, 수량 없음(무료)
        · 기본      → PRINT, 기본 수량(서버 pricing.stickerBaseQuantity)
        · 추가 수량 → PRINT, 기본 + 단위의 배수
      기본값 = 첫 색상 · 가로형 · 기본 수량(대다수 고객이 받는 구성).
    */
    const [stickerColor, setStickerColor] = useState(STICKER_COLORS[0]?.id ?? '');
    const [stickerOrientation, setStickerOrientation] = useState(STICKER_ORIENTATIONS[0].id);
    const [quantityOption, setQuantityOption] = useState(QUANTITY_OPTIONS.BASE);
    const [extraQuantity, setExtraQuantity] = useState('');
    const [isPlacementSaving, setIsPlacementSaving] = useState(false);
    /** (4) 에서 토스 카드 등록창을 닫은 횟수. [CARD_SKIP_AFTER_CANCELS] 에 닿으면 건너뛰기 버튼이 열린다. */
    const [cardRegisterCancelCount, setCardRegisterCancelCount] = useState(0);

    /*
      견적 묶음(`GET /account/install-request/quote`): 가격 상수표 + 현재 신청 기준 견적 + 기본 카드 + 결제 상태.
      PLACEMENT 는 `pricing` 으로 수량별 금액을 그리고, PAYMENT 는 `quote`(서버가 저장된 신청으로 계산한 값)를 그린다.
    */
    const [quoteView, setQuoteView] = useState(null);
    const [isQuoteLoading, setIsQuoteLoading] = useState(false);
    const [quoteError, setQuoteError] = useState(false);

    // PAYMENT State
    const [payState, setPayState] = useState('idle');   // idle | paying | success | failed
    const [payError, setPayError] = useState(null);     // {message}
    const [payResult, setPayResult] = useState(null);   // 서버 PayResponse

    // 팝업 이미지 상태 관리 — `{name, imageUrl}`
    const [previewImage, setPreviewImage] = useState(null);

    const pricing = quoteView?.pricing ?? null;
    const hasDefaultCard = !!quoteView?.defaultCard;
    /** 결제가 끝난 신청은 비치물을 바꿀 수 없다 — 바꾸면 결제 금액과 어긋난다. */
    const placementLocked = quoteView?.payment?.paid === true;

    const stickerType = stickerTypeId(stickerOrientation, stickerColor);
    const stickerFulfillment = quantityOption === QUANTITY_OPTIONS.IMAGE_ONLY
        ? STICKER_FULFILLMENT.IMAGE_ONLY : STICKER_FULFILLMENT.PRINT;
    const stickerQuantity = (() => {
        if (quantityOption === QUANTITY_OPTIONS.IMAGE_ONLY) return 0;
        if (quantityOption === QUANTITY_OPTIONS.BASE) return pricing?.stickerBaseQuantity ?? 20;
        return normalizeExtraQuantity(extraQuantity, pricing);
    })();
    const stickerNet = stickerNetOf(stickerQuantity, pricing);

    /**
     * 사업자번호 10자리를 채웠는가(3-2-5 하이픈 포함 12자). 🔑 **두 경로가 같은 판정을 쓴다**(2026-09-10).
     */
    const isBizRegNumberValid = bizRegNumber.replace(/\D/g, '').length === 10;

    // 객단가는 필수다. type="number" 라도 입력값은 문자열로 들어오고 빈 문자열이 될 수 있다.
    const isUnitPriceValid = String(unitPrice).trim() !== '' && Number(unitPrice) > 0;

    // 목록은 `constants/hosting.js` 로 옮겼다(2026-09-01) — 서버 어휘(`HostingName.kt`)와 한 곳에서 맞춘다.
    const platforms = HOSTING_MALL_PLATFORMS;

    /** 쇼핑몰 경로에서 실제 플랫폼을 골랐는가. 기본값이 '오모플레이' 라 목록 포함 여부로 판정한다. */
    const isMallPlatformSelected = platforms.some((p) => p.value === platform);

    const loadQuote = useCallback(() => {
        setIsQuoteLoading(true);
        setQuoteError(false);
        return getSignupQuote()
            .then((res) => {
                if (!res?.data?.pricing) {
                    setQuoteError(true);
                    return null;
                }
                setQuoteView(res.data);
                return res.data;
            })
            .catch(() => {
                setQuoteError(true);
                return null;
            })
            .finally(() => setIsQuoteLoading(false));
    }, []);

    /**
     * 서버에 저장된 신청값을 화면 상태로 되돌린다 — 토스에서 돌아왔을 때·'나중에 결제' 후 재진입.
     */
    const applySavedRequest = (saved) => {
        if (!saved) return;
        if (saved.linkType === LINK_TYPE_MALL) setLinkType('mall');
        const parsed = parseStickerType(saved.stickerType);
        if (parsed) {
            setStickerColor(parsed.color);
            setStickerOrientation(parsed.orientation);
        }
        if (saved.stickerFulfillment === STICKER_FULFILLMENT.IMAGE_ONLY) {
            setQuantityOption(QUANTITY_OPTIONS.IMAGE_ONLY);
        } else if (saved.stickerQuantity && saved.stickerQuantity > 20) {
            setQuantityOption(QUANTITY_OPTIONS.EXTRA);
            setExtraQuantity(String(saved.stickerQuantity));
        } else {
            setQuantityOption(QUANTITY_OPTIONS.BASE);
        }
        if (saved.logoUrl) setLogoImage(saved.logoUrl);
        if (saved.unitPrice) setUnitPrice(saved.unitPrice);
        if (saved.concept) setConcept(saved.concept);
        setIsVerified(true);
    };

    /*
      마운트: 가격표를 받고, `?step=payment` 면 저장된 신청으로 PAYMENT 에서 이어간다.

      🔑 신청이 없는데 `?step=payment` 로 오면(주소만 치고 들어옴) 처음부터 시작한다 —
         결제할 대상이 없다.
    */
    useEffect(() => {
        loadQuote();

        const params = new URLSearchParams(window.location.search);
        if (params.get('step') !== 'payment') return;

        getSignupRequest()
            .then((res) => {
                const saved = res?.data;
                if (!saved) return;
                applySavedRequest(saved);
                setStep(STEP.PAYMENT);
            })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // PAYMENT 에 들어올 때마다 견적을 다시 받는다 — 토스를 다녀오면 카드 유무가 바뀌어 있다.
    useEffect(() => {
        if (step !== STEP.PAYMENT) return;
        setPayState('idle');
        setPayError(null);
        loadQuote();
    }, [step, loadQuote]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const formatBizNumber = (input) => {
        const numbers = input.replace(/\D/g, "").slice(0, 10);
        if (numbers.length <= 3) return numbers;
        if (numbers.length <= 5)
            return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
        return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5)}`;
    };

    const goTo = (next) => {
        setIsAnimating(true);
        setTimeout(() => {
            setStep(typeof next === 'function' ? next : () => next);
            setIsAnimating(false);
        }, 500);
    };

    const nextStep = () => goTo((prev) => prev + 1);

    /**
     * 이전 스텝으로. 주소가 `/wizard` 하나라 브라우저 뒤로가기로는 이전 스텝으로 갈 수 없다(화면 안 '뒤로').
     * ⚠️ PLACEMENT 의 이전 화면은 경로에 따라 다르다 — 오모플레이 경로는 LINK 에서 MALL_* 를 건너뛴다.
     */
    const prevStep = () => goTo((prev) => {
        if (prev === STEP.PLACEMENT) return linkType === 'mall' ? STEP.MALL_INVITE_DEV : STEP.LINK;
        if (prev === STEP.PAYMENT) return STEP.PLACEMENT;
        return prev - 1;
    });

    // INTRO 는 시작점이고 DONE 은 완료 화면이라 되돌릴 것이 없다.
    const canGoBack = step > STEP.INTRO && step < STEP.DONE;

    /*
      브라우저·모바일 제스처 뒤로가기 1차 방어. 구현·주의사항은 `libs/useBackGuard.js` 주석 참고.
      (토스로 가는 `location` 이동은 popstate 가 아니라 걸리지 않는다.)
    */
    useBackGuard("'뒤로' 버튼을 이용해주세요. 다른 페이지로 넘어갈 수 있습니다.");

    const handleCheckChannel = () => {
        checkChannel(searchId).then(({data}) => {
            if (data) {
                setValidDuplicateChannel(data)
            } else {
                nextStep()
            }
        })
    }

    const handleSendCode = () => {
        kakaoProfileChannelAuth(searchId, phoneNumber)
            .then(({data}) => {
                if (data.code !== "200") {
                    alert(data.message)
                } else {
                    setIsCodeSent(true);
                    alert("인증번호가 전송되었습니다. 알림톡을 확인해 주세요.")
                }
            })
    };

    const handleVerifyCode = () => {
        channelAuthorization(searchId, phoneNumber, verificationCode)
            .then(({data}) => {
                if (data.code !== "200" && data.code !== "509" && data.code !== "514" && !data.profileKey) {
                    alert(data.message)
                } else {
                    /*
                      🔑 응답은 **신청 id** 이지 채널 id 가 아니다(2026-09-10). `localStorage.channelState` 에 넣지 않는다.
                      🔑 쇼핑몰 경로도 **사업자번호를 함께 보낸다** — 카드 등록 고유링크 본인 확인에 필요하다.
                    */
                    postSignupChannel(channelName, searchId, data.profileKey, bizRegNumber, LINK_TYPE_MALL)
                        .then(() => {
                            alert("인증 되었습니다.")
                            setIsVerified(true);
                        })
                }
            })
    };

    const openLink = (url) => {
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            fileUpload(file).then(({data}) => {
                setLogoImage(data);
                e.target.value = null
            })
        }
    };

    /**
     * 토스 카드 등록창으로 **페이지를 떠난다.** 돌아오면 `/wizard?step=payment` 다.
     * 실패·취소는 창이 닫히며 resolve 되므로 안내만 하고 그 자리에 머문다.
     */
    const startCardRegistration = () => {
        requestCardBillingAuth({successPath: CARD_REGISTRATION_RETURN_PATH})
            .catch(() => {
                alert("카드 등록창을 열지 못했습니다. 잠시 후 다시 시도해주세요.");
            });
    };

    /**
     * (4) 전용 카드 등록 — 닫힘을 **센다.** 토스 SDK 는 고객이 창을 닫으면 Promise 를 거부한다(`USER_CANCEL`).
     * 열기 실패(네트워크 등)도 거부로 오므로 함께 센다 — 둘 다 "지금 카드를 못 붙인다" 는 사실이다.
     * 취소는 고객이 한 행동이라 알럿을 띄우지 않는다. 그 외 오류만 알린다.
     */
    const startCardRegistrationFromPlacement = () => {
        requestCardBillingAuth({successPath: CARD_REGISTRATION_RETURN_PATH})
            .catch((reason) => {
                setCardRegisterCancelCount((count) => count + 1);
                if (reason?.code !== 'USER_CANCEL') {
                    alert("카드 등록창을 열지 못했습니다. 잠시 후 다시 시도해주세요.");
                }
            });
    };

    /**
     * (4) 선택값 저장. 🔑 **토스로 떠나기 전에 서버에 저장**한다 — 돌아오면 React 상태가 없다.
     * @returns 저장 성공 여부. 실패는 여기서 알리고 false.
     */
    const savePlacement = () => {
        const isMall = linkType === 'mall';
        return postSignupPlacement({
            linkType: isMall ? LINK_TYPE_MALL : LINK_TYPE_OMOPLAY,
            hostingName: isMall ? platform : HOSTING_OMOPLAY,
            hostingId: isMall ? mallId : '',
            hostingPwd: isMall ? mallPassword : '',
            stickerType,
            stickerQuantity: stickerFulfillment === STICKER_FULFILLMENT.PRINT ? stickerQuantity : null,
            stickerFulfillment,
        })
            // `fetchApi` 는 실패를 reject 하지 않고 삼킨다 — 응답 값으로 성공을 확인한다(`handleSubmit` 주석).
            .then((res) => res?.data === 'success')
            .catch(() => false)
            .then((ok) => {
                if (!ok) alert("선택 내용을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.");
                return ok;
            });
    };

    /**
     * (4) → (5)/(6). 카드가 이미 있으면(재진입·기존 고객) 토스를 건너뛰고 바로 결제 화면으로 간다.
     */
    const handlePlacementNext = () => {
        if (placementLocked) {
            goTo(STEP.PAYMENT);
            return;
        }
        if (isPlacementSaving) return;
        setIsPlacementSaving(true);

        savePlacement().then((ok) => {
            // 토스로 페이지가 떠나더라도, 창이 닫혀 돌아오는 경우를 위해 버튼은 다시 살린다.
            setIsPlacementSaving(false);
            if (!ok) return;
            if (hasDefaultCard) {
                goTo(STEP.PAYMENT);
            } else {
                startCardRegistrationFromPlacement();
            }
        });
    };

    /**
     * (4) '카드 없이 계속' — 등록창을 [CARD_SKIP_AFTER_CANCELS] 번 닫은 뒤에만 보이는 버튼.
     * 저장만 하고 토스 없이 (6) 으로 간다. (6) 은 '카드 없음' 상태를 그리고 거기서 다시 등록하거나 '나중에' 를 고른다.
     */
    const handlePlacementSkipCard = () => {
        if (isPlacementSaving) return;
        setIsPlacementSaving(true);
        savePlacement().then((ok) => {
            setIsPlacementSaving(false);
            if (ok) goTo(STEP.PAYMENT);
        });
    };

    /** 건너뛰기 버튼을 보일 조건. 카드가 있거나 결제가 끝난 신청에는 애초에 필요 없다. */
    const canSkipCardRegistration =
        !placementLocked && !hasDefaultCard && cardRegisterCancelCount >= CARD_SKIP_AFTER_CANCELS;

    /**
     * (6) 결제하기. 🔴 **실제 카드 승인.** 금액은 보내지 않는다 — 서버가 저장된 신청으로 계산한다.
     * 실패도 200 으로 온다(`data.result`). 카드사 사유를 화면에 그대로 남긴다.
     */
    const handlePay = () => {
        const amount = quoteView?.quote?.amount;
        if (payState === 'paying' || amount == null) return;
        if (!window.confirm(`등록된 카드로 ${won(amount)}을 결제합니다. 계속할까요?`)) return;

        setPayState('paying');
        setPayError(null);

        postSignupPay()
            .then((res) => {
                const data = res?.data;
                if (data?.result === PAY_RESULT.SUCCESS || data?.result === PAY_RESULT.ALREADY_PAID) {
                    setPayResult(data);
                    setPayState('success');
                    loadQuote();
                    return;
                }
                setPayState('failed');
                setPayError({
                    result: data?.result,
                    message: data?.message ?? '결제에 실패했습니다. 잠시 후 다시 시도해 주세요.',
                });
            })
            .catch(() => {
                setPayState('failed');
                setPayError({message: '결제 요청 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.'});
            });
    };

    /** '나중에 결제하기' — 명세의 bypass. 세팅이 시작되지 않는다는 것을 알리고 넘어간다. */
    const handleSkipPayment = () => {
        if (!window.confirm(
            "지금 결제하지 않으면 초기 세팅비 결제가 확인될 때까지 세팅이 시작되지 않습니다.\n" +
            "서비스 목록 화면에서 언제든 결제할 수 있습니다.\n나중에 결제할까요?"
        )) return;
        goTo(STEP.BENEFIT_EXAMPLE);
    };

    /**
     * (8) 최종 제출 — 매장 정보만 보낸다. 비치물·연동 방식은 PLACEMENT 가 이미 저장했다
     * (서버는 비치물 칸이 payload 에 없으면 건드리지 않는다 — 여기서 보내면 NULL 로 덮인다).
     */
    const handleSubmit = () => {
        if (!isUnitPriceValid || isSubmitting) return

        setIsSubmitting(true)

        // 객단가는 숫자로 보낸다 — input 값은 문자열이다.
        postSignupSubmit({
            logoUrl: logoImage,
            unitPrice: Number(unitPrice),
            concept,
        })
            .then((res) => {
                /*
                  성공 여부를 **응답 값으로** 확인한다. `fetchApi` 는 실패를 reject 하지 않고 삼킨다
                  (`src/api/fetch.js`) — 종전 코드는 실패해도 완료 화면으로 넘어갔다.
                */
                if (res?.data === "success") {
                    nextStep()
                    return
                }

                setIsSubmitting(false)
                alert("등록에 실패했습니다. 잠시 후 다시 시도해 주세요.\n계속 실패하면 담당자에게 알려주세요.")
            })
            .catch(() => {
                setIsSubmitting(false)
                alert("등록에 실패했습니다. 잠시 후 다시 시도해 주세요.\n계속 실패하면 담당자에게 알려주세요.")
            })
    }

    const handleLogout = () => {
        logout()
    };

    const primaryButton = "w-full bg-blue-600 text-white rounded-xl py-4 font-semibold text-[16px] disabled:bg-gray-200 disabled:text-gray-400 hover:bg-blue-700 active:scale-[0.98] transition-all";
    const secondaryButton = "w-full bg-white text-gray-600 border border-gray-300 rounded-xl py-3.5 font-semibold text-[15px] hover:bg-gray-50 transition-colors";

    const isPaid = payState === 'success' || placementLocked;

    return (
        /*
          레이아웃 기준 (2026-08-21): 상단 정렬(`m-auto` 없음) · `min-h-[100dvh]` · 카드 폭 `max-w-[560px]`.
          내용 길이가 스텝마다 달라 세로 중앙정렬이면 카드가 튄다. `common.css` 가 `img { max-width: unset }` 라
          이미지에 직접 폭을 줘야 줄어든다.
        */
        <div className="min-h-screen min-h-[100dvh] bg-gray-50 flex flex-col px-3 py-5 sm:p-4">
            <style>{`
        @keyframes slideUpFade {
          0% { opacity: 0; transform: translateY(15px); filter: blur(3px); }
          100% { opacity: 1; transform: translateY(0); filter: blur(0px); }
        }
        .animate-slide-up {
          animation: slideUpFade 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }
        .fade-out {
          opacity: 0;
          transform: translateY(-10px);
          filter: blur(2px);
          transition: all 0.5s ease;
        }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

            <div
                className={`relative mx-auto w-full max-w-[560px] bg-white rounded-3xl shadow-sm p-5 sm:p-8 ${isAnimating ? 'fade-out' : 'animate-slide-up'}`}
            >
                {/* 공통 헤더 행 — '뒤로'·'로그아웃' 을 카드 맨 위 한 줄로(2026-08-21). 제목은 카드 폭을 온전히 쓴다. */}
                <div className="flex items-center justify-between mb-3 -mt-1">
                    {canGoBack ? (
                        <button
                            onClick={prevStep}
                            className="flex items-center gap-1 -ml-1 px-1.5 py-1 text-[13px] text-gray-500 hover:text-gray-800 font-medium transition-colors rounded-md hover:bg-gray-50"
                        >
                            <ChevronLeft className="w-4 h-4" />
                            뒤로
                        </button>
                    ) : (
                        <span />
                    )}
                    <button
                        onClick={handleLogout}
                        className="flex items-center gap-1.5 px-1.5 py-1 text-[13px] text-gray-400 hover:text-gray-600 font-medium transition-colors rounded-md hover:bg-gray-50"
                    >
                        <LogOut className="w-3.5 h-3.5" />
                        로그아웃
                    </button>
                </div>

                {/* ================= INTRO (2) 오모플레이 설명 ================= */}
                {step === STEP.INTRO && (
                    <div className="flex flex-col h-full">
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">오모플레이는 이렇게 동작해요</h2>
                        <p className="text-gray-500 mb-5 text-sm">설치 담당자가 이 화면으로 서비스를 간단히 설명드립니다.</p>
                        {/*
                          통이미지 한 장. 🟡 지금은 **플레이스홀더(SVG)** 다 — 담당자가 실제 이미지로
                          `assets/images/wizard/intro_overview.svg` 를 교체한다(파일명을 바꾸면 import 도 바꿀 것).
                        */}
                        <img src={IntroOverview} alt="오모플레이 서비스 소개" className="w-full h-auto rounded-2xl border border-gray-200 mb-6" />
                        <button onClick={nextStep} className={primaryButton}>다음으로</button>
                    </div>
                )}

                {/* ================= LINK (3) 연동 방식 ================= */}
                {step === STEP.LINK && (
                    <div className="flex flex-col h-full">
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">홈페이지 연동 방식을 선택 해주세요.</h2>
                        <p className="text-gray-500 mb-6 text-sm">오모플레이 세팅 및 카카오 싱크 연동을 위해 필요합니다.</p>

                        <div className="flex flex-col sm:flex-row gap-3 mb-6">
                            <button
                                onClick={() => setLinkType('omoplay')}
                                className={`flex-1 flex items-center justify-center gap-2 py-4 px-4 border rounded-xl font-semibold text-sm transition-all active:scale-[0.99] ${
                                    linkType === 'omoplay'
                                        ? 'bg-blue-600 border-blue-600 text-white shadow-sm hover:bg-blue-700'
                                        : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                {linkType === 'omoplay' && <CheckCircle2 className="w-4 h-4 shrink-0" />}
                                <span>오모플레이를 이용하겠습니다</span>
                            </button>

                            <button
                                onClick={() => setLinkType('mall')}
                                className={`flex-1 flex items-center justify-center gap-2 py-4 px-4 border rounded-xl font-semibold text-sm transition-all active:scale-[0.99] ${
                                    linkType === 'mall'
                                        ? 'bg-blue-600 border-blue-600 text-white shadow-sm hover:bg-blue-700'
                                        : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                {linkType === 'mall' && <CheckCircle2 className="w-4 h-4 shrink-0" />}
                                <span>다른 자사몰과 연동하겠습니다</span>
                            </button>
                        </div>

                        {/*
                          사업자 등록 번호 — **두 경로 공통**(2026-09-10). 카드 등록 고유링크 본인 확인에 필요하다.
                          '인증' 하지 않고 **입력만** 받는다(2026-08-20) — 모먼트 사업자 조회는 신규 고객이 통과할 수 없다.
                        */}
                        <div className="mb-6">
                            <label htmlFor="bizRegNumber" className="block text-sm font-semibold text-gray-700 mb-1.5">
                                사업자 등록 번호
                            </label>
                            <input
                                id="bizRegNumber"
                                type="text"
                                inputMode="numeric"
                                value={bizRegNumber}
                                onChange={(e) => setBizRegNumber(formatBizNumber(e.target.value))}
                                placeholder="123-45-67890"
                                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3.5 outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                            />
                        </div>

                        {linkType === 'mall' && (
                            <>
                                <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                    <div className="space-y-4 mb-6 relative">
                                        <div ref={dropdownRef} className="relative z-20">
                                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">세팅할 플랫폼</label>
                                            <div onClick={() => setIsDropdownOpen(!isDropdownOpen)} className={`w-full flex items-center justify-between border rounded-xl px-4 py-3.5 cursor-pointer transition-all ${isDropdownOpen ? 'bg-white border-blue-500 ring-2 ring-blue-100' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}`}>
                                            <span className="text-gray-900 font-medium">
                                                {platforms.find(p => p.value === platform)?.label || '플랫폼 선택'}
                                            </span>
                                                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                                            </div>
                                            {isDropdownOpen && (
                                                <div className="absolute top-full left-0 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto no-scrollbar z-30 animate-in fade-in slide-in-from-top-2">
                                                    {platforms.map((p, idx) => (
                                                        <div key={idx} onClick={() => { setPlatform(p.value); setIsDropdownOpen(false); }} className={`px-4 py-3.5 hover:bg-gray-50 cursor-pointer text-sm transition-colors ${platform === p.value ? 'bg-blue-50 font-semibold text-blue-600' : 'text-gray-700'}`}>{p.label}</div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        <div className="relative z-10 transition-all duration-300">
                                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">쇼핑몰 관리자 아이디</label>
                                            <input type="text" name="hosting-id" autoComplete="off" value={mallId} onChange={(e) => setMallId(e.target.value)} placeholder="아이디 입력" className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3.5 outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all" />
                                        </div>
                                        <div className="relative z-10">
                                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">비밀번호</label>
                                            <input type="password" name="hosting-pwd" autoComplete="new-password" value={mallPassword} onChange={(e) => setMallPassword(e.target.value)} placeholder="비밀번호 입력" className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3.5 outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all" />
                                        </div>
                                    </div>

                                    <div className="bg-[#FFF8E7] rounded-2xl p-5 mb-6 border border-[#FDEBCA]">
                                        <div className="flex items-center gap-1.5 mb-2"><ShieldAlert className="w-5 h-5 text-[#F59E0B]" /><h4 className="font-bold text-[#D97706] text-sm">계정정보가 왜 필요한가요?</h4></div>
                                        <ul className="text-[13px] text-[#92400E] leading-relaxed space-y-1.5 pl-1">
                                            <li>• 카카오 싱크 설정, 회원가입 필수정보 설정, 약관 확인을 위해 필요합니다.</li>
                                            <li>• 제공해주신 정보는 안전하게 암호화되어 보관되며, 최소한의 필요 인원 외에는 접근할 수 없도록 관리하고 있습니다.</li>
                                            <li>• 계정 정보가 부정확한 경우 세팅이 지연될 수 있습니다.</li>
                                        </ul>
                                    </div>
                                </div>
                                <button
                                    onClick={nextStep}
                                    // 플랫폼도 반드시 골라야 한다(2026-09-01). 기본값이 '오모플레이' 라 막지 않으면 그대로 저장된다.
                                    disabled={!isBizRegNumberValid || !isMallPlatformSelected || !mallId || !mallPassword}
                                    className={`${primaryButton} mt-auto`}
                                >
                                    다음으로
                                </button>
                            </>
                        )}

                        {linkType !== 'mall' && (
                            <>
                                <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                    <div className="bg-[#FFF8E7] rounded-2xl p-5 mb-6 border border-[#FDEBCA]">
                                        <div className="flex items-center gap-1.5 mb-2"><ShieldAlert className="w-5 h-5 text-[#F59E0B]" /><h4 className="font-bold text-[#D97706] text-sm">오모플레이 홈페이지</h4></div>
                                        <ul className="text-[13px] text-[#92400E] leading-relaxed space-y-1.5 pl-1">
                                            <li>• 오모플레이에서 별도의 홈페이지를 제공 해드립니다.</li>
                                            <li>• 기본적으로 연동에 필요한 홈페이지만 제공 해드리며 실제 운영이 필요한 경우 쇼핑몰로 연동 해주세요.</li>
                                        </ul>
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        /*
                                          신청을 `channel_install_request` 에 접수한다(2026-09-10). ⛔ 채널을 만들지 않는다.
                                          🔑 사업자번호가 신청서에 남는 것이 핵심 — 관리자 위저드가 그 번호로 이어받는다. 멱등(계정당 1건).
                                        */
                                        postSignupChannel(channelName, searchId, undefined, bizRegNumber, LINK_TYPE_OMOPLAY)
                                            .then(() => {
                                                alert("연동 신청이 접수되었습니다.\n오모플레이 담당자가 순차적으로 카카오 채널과의 연동 작업을 진행합니다.")
                                                setIsVerified(true);
                                                goTo(STEP.PLACEMENT)
                                            })
                                    }}
                                    disabled={!isBizRegNumberValid}
                                    className={`${primaryButton} mt-auto`}
                                >
                                    다음으로
                                </button>
                            </>
                        )}
                    </div>
                )}

                {/* ================= MALL_CHANNEL ================= */}
                {step === STEP.MALL_CHANNEL && (
                    <div className="flex flex-col h-full w-full">
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">채널 정보를 입력해주세요</h2>
                        <p className="text-gray-500 mb-6 text-sm">카카오톡 채널 이름과 검색용 아이디가 필요해요.</p>

                        <div className="mb-6 rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm flex flex-col">
                            <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div className="w-2.5 h-2.5 rounded-full bg-[#FEE500] shadow-sm"></div>
                                    <span className="text-[12px] font-bold text-gray-600">카카오 비즈니스 관리자 화면 예시</span>
                                </div>
                            </div>

                            <div className="p-4 flex items-center bg-white opacity-80 pointer-events-none">
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-300 to-purple-300 flex items-center justify-center text-white font-bold text-xl shrink-0">
                                    m
                                </div>
                                <div className="ml-4 flex-1">
                                    <div className="flex items-center gap-1">
                                        <span className="font-bold text-gray-800 text-[15px]">{channelName || '오모플레이(OMOPlay)'}</span>
                                        <CheckCircle2 className="w-[14px] h-[14px] text-blue-400 fill-blue-50" />
                                        <span className="text-[11px] text-red-400 ml-1 font-medium">마스터</span>
                                    </div>
                                    <div className="text-[13px] text-gray-500 mt-0.5">@{searchId || '오모플레이'}</div>
                                </div>
                            </div>

                            <button
                                onClick={() => openLink('https://business.kakao.com/profiles')}
                                className="w-full flex items-center justify-center gap-2 py-3.5 bg-gray-50 hover:bg-gray-100 border-t border-gray-200 text-[14px] font-semibold text-gray-700 transition-colors active:bg-gray-200"
                            >
                                <ExternalLink className="w-4 h-4 text-gray-500" />
                                내 채널 정보 확인하러 가기
                            </button>
                        </div>

                        <div className="space-y-4 flex-1 mb-6">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">채널 이름</label>
                                <input
                                    type="text"
                                    value={channelName}
                                    onChange={(e) => setChannelName(e.target.value)}
                                    placeholder="예: 오모플레이(OMOPlay)"
                                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3.5 outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">검색용 아이디</label>
                                <div className="flex items-center w-full bg-gray-50 border border-gray-200 rounded-xl px-4 focus-within:bg-white focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
                                    <span className="text-gray-500 font-medium">@</span>
                                    <input
                                        type="text"
                                        value={searchId}
                                        onChange={(e) => setSearchId(e.target.value.replace(/@/g, ''))}
                                        placeholder="오모플레이"
                                        className="w-full bg-transparent py-3.5 pl-1 outline-none text-gray-900 placeholder-gray-400"
                                    />
                                </div>
                            </div>
                            {
                                validDuplicateChannel && (<div className="text-red-500">이미 등록된 채널입니다.</div>)
                            }
                        </div>

                        <button
                            onClick={handleCheckChannel}
                            disabled={channelName.length < 2 || searchId.length < 2}
                            className={`${primaryButton} mt-auto`}
                        >
                            다음으로
                        </button>
                    </div>
                )}

                {/* ================= MALL_AUTH ================= */}
                {step === STEP.MALL_AUTH && (
                    <div className="flex flex-col h-full">
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">관리자 인증을 진행해주세요</h2>
                        <p className="text-gray-500 mb-6 text-sm">카카오 채널 소유권 확인을 위해 필수적인 단계입니다.</p>

                        <button
                            onClick={() => openLink('https://omoplay.notion.site/NEW-App-25a2d9e7196a809e943ef4c286d0e1ac')}
                            className="flex items-center justify-center gap-2 w-full mb-6 text-blue-600 bg-blue-50 hover:bg-blue-100 py-3 rounded-xl text-sm font-medium transition-colors"
                        >
                            <BookOpen className="w-4 h-4" />
                            신청 가이드 - 카카오 채널 관리자 인증 방법
                        </button>

                        <div className="space-y-4 flex-1 mb-6">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">관리자 휴대폰 번호</label>
                                <div className="flex flex-col sm:flex-row gap-2">
                                    <input
                                        type="tel"
                                        value={phoneNumber}
                                        onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9-]/g, ''))}
                                        disabled={isVerified}
                                        placeholder="번호만 입력해주세요"
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3.5 outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all disabled:opacity-50 disabled:bg-gray-100"
                                    />
                                    <button
                                        onClick={handleSendCode}
                                        disabled={!phoneNumber || isVerified}
                                        className="w-full sm:w-auto px-4 py-3.5 bg-gray-800 text-white rounded-xl font-semibold text-[14px] hover:bg-gray-900 transition-colors whitespace-nowrap disabled:bg-gray-200 disabled:text-gray-400"
                                    >
                                        {isCodeSent ? '재전송' : '인증번호 받기'}
                                    </button>
                                </div>
                            </div>

                            {isCodeSent && (
                                <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">인증번호</label>
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={6}
                                            value={verificationCode}
                                            onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, ''))}
                                            disabled={isVerified}
                                            placeholder="인증번호 6자리"
                                            className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3.5 outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all disabled:opacity-50 disabled:bg-gray-100"
                                        />
                                        <button
                                            onClick={handleVerifyCode}
                                            disabled={verificationCode.length !== 6 || isVerified}
                                            className="w-full sm:w-auto px-6 py-3.5 bg-blue-600 text-white rounded-xl font-semibold text-[14px] hover:bg-blue-700 transition-colors whitespace-nowrap disabled:bg-gray-200 disabled:text-gray-400"
                                        >
                                            {isVerified ? '인증 완료' : '인증 확인'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="bg-[#FFF8E7] rounded-2xl p-4 mb-6 border border-[#FDEBCA]">
                            <div className="flex items-center gap-1.5 mb-1.5">
                                <Info className="w-4 h-4 text-[#F59E0B]" />
                                <h4 className="font-bold text-[#D97706] text-sm">카카오 채널 관리자 인증이 왜 필요한가요?</h4>
                            </div>
                            <p className="text-[13px] text-[#92400E] leading-relaxed pl-5">
                                카카오 채널의 소유권을 안전하게 확인하기 위한 절차입니다.
                            </p>
                        </div>

                        <button
                            onClick={nextStep}
                            disabled={!isVerified}
                            className={`${primaryButton} mt-auto`}
                        >
                            다음으로
                        </button>
                    </div>
                )}

                {/* ================= MALL_INVITE_BIZ ================= */}
                {step === STEP.MALL_INVITE_BIZ && (
                    <div className="flex flex-col h-full">
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">비즈니스 관리자로 초대해 주세요</h2>
                        <p className="text-gray-500 mb-6 text-sm">오모플레이 계정을 카카오 비즈니스 관리자로 등록해야 연결이 가능합니다.</p>

                        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 mb-6 text-center">
                            <p className="text-sm text-gray-500 mb-2">초대할 계정</p>
                            <div className="flex items-center justify-center gap-2">
                                <span className="text-lg font-bold text-gray-900">omoplay@kakao.com</span>
                                <button className="p-1.5 text-gray-400 hover:text-gray-700 bg-white border rounded-lg shadow-sm active:scale-95 transition-all" onClick={() => navigator.clipboard.writeText('omoplay@kakao.com')}><Copy className="w-4 h-4" /></button>
                            </div>
                        </div>

                        <button
                            onClick={() => openLink(`https://business.kakao.com/profiles`)}
                            className="flex items-center justify-center gap-2 w-full mb-3 text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 py-3 rounded-xl text-sm font-medium transition-colors shadow-sm active:scale-[0.98]"
                        >
                            <ExternalLink className="w-4 h-4 text-gray-400" />
                            카카오 비즈니스 관리자 바로가기
                        </button>

                        <button
                            onClick={() => openLink('https://omoplay.notion.site/2332d9e7196a81389540de68148cfc8d')}
                            className="flex items-center justify-center gap-2 w-full mb-8 text-blue-600 bg-blue-50 hover:bg-blue-100 py-3 rounded-xl text-sm font-medium transition-colors"
                        >
                            <BookOpen className="w-4 h-4" />
                            신청 가이드 - 카카오 채널에 오모플레이 계정 초대하기
                        </button>

                        <button onClick={nextStep} className={`${primaryButton} mt-auto`}>
                            계정 초대를 완료했습니다
                        </button>
                    </div>
                )}

                {/* ================= MALL_INVITE_DEV ================= */}
                {step === STEP.MALL_INVITE_DEV && (
                    <div className="flex flex-col h-full">
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">디벨로퍼 앱 초대하기</h2>
                        <p className="text-gray-500 mb-5 text-sm">원활한 서비스 연동을 위해 카카오 디벨로퍼 앱 초대가 필요합니다.</p>

                        <div className="bg-red-50 border border-red-100 rounded-2xl p-4 mb-3">
                            <p className="text-[14px] text-red-800 font-bold flex items-center gap-2">
                                <AlertCircle className="w-5 h-5 text-red-600" />
                                초대 시 반드시 Editor 권한으로 추가해 주세요.
                            </p>
                        </div>

                        <div className="bg-[#FFF8E7] border border-[#FDEBCA] rounded-2xl p-4 mb-6 space-y-2.5">
                            <p className="text-[13px] text-[#92400E] leading-relaxed flex items-start gap-1.5">
                                <span className="text-[#F59E0B] mt-0.5 font-bold">•</span>
                                <span>기존에 사용하던 앱이 없다면 <strong>앱을 새로 생성</strong>해 주세요.</span>
                            </p>
                            <p className="text-[13px] text-[#92400E] leading-relaxed flex items-start gap-1.5">
                                <span className="text-[#F59E0B] mt-0.5 font-bold">•</span>
                                <span>현재 쇼핑몰에서 카카오싱크를 사용 중이라면 <strong>반드시 기존 앱에서 초대해 주세요.</strong><br/>기존 앱과 다른 경우 세팅이 지연될 수 있습니다.</span>
                            </p>
                        </div>

                        <button
                            onClick={() => openLink('https://developers.kakao.com/console/app')}
                            className="flex items-center justify-center gap-2 w-full mb-3 text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 py-3 rounded-xl text-sm font-medium transition-colors shadow-sm active:scale-[0.98]"
                        >
                            <ExternalLink className="w-4 h-4 text-gray-400" />
                            카카오 디벨로퍼스 바로가기
                        </button>

                        <button
                            onClick={() => openLink('https://omoplay.notion.site/2332d9e7196a81f7842ad793bfa3a471')}
                            className="flex items-center justify-center gap-2 w-full mb-8 text-blue-600 bg-blue-50 hover:bg-blue-100 py-3 rounded-xl text-sm font-medium transition-colors"
                        >
                            <BookOpen className="w-4 h-4" />
                            신청 가이드 - 카카오 디벨로퍼 앱 초대하기
                        </button>

                        <button onClick={nextStep} className={`${primaryButton} mt-auto`}>
                            앱 관리자 초대를 완료했습니다
                        </button>
                    </div>
                )}

                {/*
                  ================= PLACEMENT (4) 이벤트 QR 비치 방법 =================

                  2026-10-12 개편 — POP 제거. 스티커 **색상 → 가로/세로 → 수량(이미지만/기본/추가)** 순으로 고르고
                  맨 아래 선택 내용을 요약한다. 금액은 서버 `pricing` 으로만 그린다.
                */}
                {step === STEP.PLACEMENT && (
                    <div className="flex flex-col h-full">
                        <div className="inline-block bg-indigo-50 text-indigo-600 px-3 py-1 rounded-full text-[13px] font-bold mb-3 w-max mt-1">
                            🎉 이제 거의 세팅이 완료되었어요!
                        </div>
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">이벤트 QR을 비치할 방법을 선택하세요</h2>
                        <p className="text-gray-500 mb-6 text-sm">고객이 QR 을 찍는 스티커입니다. 색상과 방향을 고르고, 제작 수량을 정해주세요.</p>

                        {placementLocked && (
                            <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-2">
                                <Lock className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                                <p className="text-[13px] text-emerald-800 leading-relaxed">
                                    초기 세팅비 결제가 끝나 스티커 선택을 바꿀 수 없습니다. 변경이 필요하면 담당자에게 알려주세요.
                                </p>
                            </div>
                        )}

                        {/* 사용 예시 — 테이블 부착 / 테이블 오더 두 장만(명세) */}
                        <div className="mb-7">
                            <label className="block text-sm font-semibold text-gray-700 mb-3">사용 예시</label>
                            <div className="grid grid-cols-2 gap-3">
                                <figure className="m-0">
                                    <img src={Sticker1} alt="테이블 부착 예시" className="w-full h-auto rounded-xl border border-gray-200" />
                                    <figcaption className="mt-1.5 text-center text-[12px] text-gray-500">테이블 부착 예시</figcaption>
                                </figure>
                                <figure className="m-0">
                                    <img src={Sticker3} alt="테이블 오더 예시" className="w-full h-auto rounded-xl border border-gray-200" />
                                    <figcaption className="mt-1.5 text-center text-[12px] text-gray-500">테이블 오더 예시</figcaption>
                                </figure>
                            </div>
                        </div>

                        {/* 색상 */}
                        <div className="mb-7">
                            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                                <Layers className="w-4 h-4" /> 스티커 색상
                            </label>
                            <div className="grid grid-cols-3 gap-3">
                                {STICKER_COLORS.map((color) => {
                                    const imageUrl = stickerImageUrl(stickerOrientation, color.id);
                                    const selected = stickerColor === color.id;
                                    return (
                                        <div key={color.id} className="flex flex-col gap-2">
                                            {/*
                                              썸네일을 누르면 **선택**된다(2026-10-12 사용자 요청). 종전에는 썸네일 = 확대, 라디오 = 선택이라
                                              가장 큰 클릭 영역이 선택이 아니었다. 확대는 아래 '크게 보기' 로 옮겼다.
                                              결제 후 잠김 상태에서는 썸네일도 선택을 바꾸지 않는다(라디오 disabled 와 같은 규칙).
                                            */}
                                            <div
                                                role="button"
                                                aria-label={`${color.label} 선택`}
                                                data-testid={`sticker-color-thumb-${color.id}`}
                                                onClick={() => { if (!placementLocked) setStickerColor(color.id); }}
                                                className={`relative aspect-square rounded-xl overflow-hidden border shadow-sm transition-all ${
                                                    placementLocked ? 'cursor-not-allowed' : 'cursor-pointer hover:shadow-md'
                                                } ${selected ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200 hover:border-gray-300'}`}
                                            >
                                                <img src={imageUrl} alt={color.label} className="w-full h-full object-contain" />
                                                {selected && (
                                                    <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center shadow">
                                                        <Check className="w-3 h-3 text-white stroke-[3]" />
                                                    </div>
                                                )}
                                            </div>
                                            <label className="flex items-center justify-center gap-2 cursor-pointer group">
                                                <input
                                                    type="radio"
                                                    name="sticker_color"
                                                    value={color.id}
                                                    checked={selected}
                                                    disabled={placementLocked}
                                                    onChange={() => setStickerColor(color.id)}
                                                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
                                                />
                                                <span className={`text-sm font-medium transition-colors ${selected ? 'text-blue-700 font-bold' : 'text-gray-600 group-hover:text-gray-900'}`}>
                                                    {color.label}
                                                </span>
                                            </label>
                                            {/* 확대 보기 — 라디오·라벨 아래 돋보기. 선택과 분리된 별도 버튼이라 잠김 상태에서도 볼 수 있다. */}
                                            <button
                                                type="button"
                                                data-testid={`sticker-color-zoom-${color.id}`}
                                                onClick={() => setPreviewImage({name: describeStickerType(stickerTypeId(stickerOrientation, color.id)), imageUrl})}
                                                className="mx-auto flex items-center gap-1 px-2 py-1 rounded-md text-[12px] text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors"
                                            >
                                                <ZoomIn className="w-3.5 h-3.5" />
                                                크게 보기
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* 가로형 / 세로형 */}
                        <div className="mb-7">
                            <label className="block text-sm font-semibold text-gray-700 mb-3">스티커 방향</label>
                            <div className="grid grid-cols-2 gap-3">
                                {STICKER_ORIENTATIONS.map((o) => {
                                    const selected = stickerOrientation === o.id;
                                    return (
                                        <label
                                            key={o.id}
                                            className={`flex items-center gap-3 p-4 rounded-2xl border cursor-pointer transition-all ${
                                                selected ? 'bg-blue-50 border-blue-500 ring-1 ring-blue-200' : 'bg-white border-gray-200 hover:bg-gray-50'
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="sticker_orientation"
                                                value={o.id}
                                                checked={selected}
                                                disabled={placementLocked}
                                                onChange={() => setStickerOrientation(o.id)}
                                                className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
                                            />
                                            <span className="flex flex-col">
                                                <span className={`text-sm font-semibold ${selected ? 'text-blue-700' : 'text-gray-700'}`}>{o.label}</span>
                                                <span className="text-[12px] text-gray-500">{o.spec}</span>
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                            <p className="mt-2 text-[12px] text-gray-400">방수형 스티커 · 같은 비율을 세로로 돌린 형태입니다.</p>
                        </div>

                        {/* 수량 — 이미지만 / 기본 / 추가 */}
                        <div className="mb-7">
                            <label className="block text-sm font-semibold text-gray-700 mb-3">매장 QR 스티커 수량</label>
                            {quoteError && (
                                <p className="mb-3 text-[13px] text-red-600">가격 정보를 불러오지 못했습니다. 새로고침 후 다시 시도해주세요.</p>
                            )}
                            <div className="space-y-3">
                                <QuantityOption
                                    name="sticker_quantity"
                                    value={QUANTITY_OPTIONS.IMAGE_ONLY}
                                    selected={quantityOption === QUANTITY_OPTIONS.IMAGE_ONLY}
                                    disabled={placementLocked}
                                    onSelect={setQuantityOption}
                                    title="이미지만 받을게요"
                                    price="무료"
                                    lines={[
                                        'QR코드와 스티커 이미지는 무상으로 제공해 드립니다.',
                                        '테이블 오더 기기에 넣으시거나, 원하는 디자인으로 직접 제작하실 수 있습니다.',
                                    ]}
                                />
                                <QuantityOption
                                    name="sticker_quantity"
                                    value={QUANTITY_OPTIONS.BASE}
                                    selected={quantityOption === QUANTITY_OPTIONS.BASE}
                                    disabled={placementLocked}
                                    onSelect={setQuantityOption}
                                    title={pricing ? `${pricing.stickerBaseQuantity}장` : '기본 수량'}
                                    price={pricing ? `${won(pricing.stickerBaseNet)} (VAT 별도)` : ''}
                                    lines={['매장까지 택배로 보내드립니다.']}
                                />
                                <QuantityOption
                                    name="sticker_quantity"
                                    value={QUANTITY_OPTIONS.EXTRA}
                                    selected={quantityOption === QUANTITY_OPTIONS.EXTRA}
                                    disabled={placementLocked}
                                    onSelect={setQuantityOption}
                                    title="추가 수량"
                                    price={pricing ? `${pricing.stickerStepQuantity}장마다 +${won(pricing.stickerStepNet)} (VAT 별도)` : ''}
                                    lines={pricing ? [`${pricing.stickerBaseQuantity}장에 ${pricing.stickerStepQuantity}장 단위로 추가합니다.`] : []}
                                >
                                    {quantityOption === QUANTITY_OPTIONS.EXTRA && (
                                        <div className="mt-3 flex flex-wrap items-center gap-3">
                                            <label htmlFor="sticker_extra_quantity" className="text-sm font-semibold text-gray-700">수량</label>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    id="sticker_extra_quantity"
                                                    type="number"
                                                    inputMode="numeric"
                                                    min={(pricing?.stickerBaseQuantity ?? 20) + (pricing?.stickerStepQuantity ?? 10)}
                                                    step={pricing?.stickerStepQuantity ?? 10}
                                                    value={extraQuantity}
                                                    disabled={placementLocked}
                                                    placeholder={String((pricing?.stickerBaseQuantity ?? 20) + (pricing?.stickerStepQuantity ?? 10))}
                                                    onChange={(e) => setExtraQuantity(e.target.value)}
                                                    /*
                                                      🔑 **정규화는 blur 에서 한다.** onChange 에서 올림하면 "3" 을 치는 순간 점프해 뒷자리를 못 친다.
                                                         올림이다 — 내림하면 고객이 요청한 것보다 적게 보낸다.
                                                    */
                                                    onBlur={() => setExtraQuantity(String(normalizeExtraQuantity(extraQuantity, pricing)))}
                                                    className="w-28 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                                                />
                                                <span className="text-sm text-gray-600">장</span>
                                            </div>
                                            <span className="text-[13px] text-gray-500">{pricing?.stickerStepQuantity ?? 10}장 단위</span>
                                        </div>
                                    )}
                                </QuantityOption>
                            </div>
                        </div>

                        {/* 선택 내용 종합 */}
                        <div className="mb-6 rounded-2xl border border-gray-200 bg-gray-50 p-4" data-testid="placement-summary">
                            <div className="text-sm font-semibold text-gray-700 mb-2">선택한 내용</div>
                            <dl className="text-[13px] text-gray-600 space-y-1">
                                <div className="flex justify-between"><dt>색상</dt><dd className="font-medium text-gray-900">{STICKER_COLORS.find((c) => c.id === stickerColor)?.label ?? '-'}</dd></div>
                                <div className="flex justify-between"><dt>방향</dt><dd className="font-medium text-gray-900">{STICKER_ORIENTATIONS.find((o) => o.id === stickerOrientation)?.label} ({STICKER_ORIENTATIONS.find((o) => o.id === stickerOrientation)?.spec})</dd></div>
                                <div className="flex justify-between"><dt>수량</dt><dd className="font-medium text-gray-900">{stickerQuantity > 0 ? `${stickerQuantity}장` : '이미지만 (무료)'}</dd></div>
                                <div className="flex justify-between border-t border-gray-200 pt-2 mt-2">
                                    <dt className="font-semibold">스티커 비용</dt>
                                    <dd className="font-bold text-gray-900">{won(stickerNet)} <span className="font-normal text-gray-500 text-[12px]">(VAT 별도)</span></dd>
                                </div>
                            </dl>
                        </div>

                        <button
                            onClick={handlePlacementNext}
                            disabled={isPlacementSaving || !stickerColor || (!pricing && !placementLocked)}
                            className={`${primaryButton} mt-auto`}
                        >
                            {isPlacementSaving ? '저장 중...' : (placementLocked || hasDefaultCard ? '다음으로' : '결제수단 등록하기')}
                        </button>

                        {/*
                          카드 등록창을 두 번 닫은 고객에게만 열리는 탈출구(2026-10-12 사용자 결정).
                          처음부터 보이면 카드 등록을 미루는 고객이 늘고, 없으면 카드가 준비 안 된 고객이 여기서 막힌다.
                        */}
                        {canSkipCardRegistration && (
                            <div className="mt-3" data-testid="skip-card-registration">
                                {/*
                                  색을 넣은 버튼이다(2026-10-12 사용자 피드백 — 흰 배경 + 회색 테두리는 "플랫해서 버튼으로 안 보였다").
                                  주 동작(파랑)과는 구분돼야 하므로 중립적인 진회색을 쓴다(`init-setting` '저장만 하기' 와 같은 선택).
                                */}
                                <button
                                    onClick={handlePlacementSkipCard}
                                    disabled={isPlacementSaving || !stickerColor || !pricing}
                                    className="w-full bg-slate-600 text-white rounded-xl py-3.5 font-semibold text-[15px] shadow-sm hover:bg-slate-700 active:scale-[0.98] disabled:bg-gray-200 disabled:text-gray-400 transition-all"
                                >
                                    카드 없이 계속 (나중에 등록)
                                </button>
                                <p className="mt-2 text-[12px] text-gray-500 leading-relaxed text-center">
                                    결제수단이 아직 준비되지 않았다면 결제수단 등록 없이 먼저 신청을 마무리 해주세요.<br />
                                    별도의 결제수단 등록용 링크를 보내드리며, 세팅은 설치비 결제 후에 시작됩니다.
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/*
                  ================= PAYMENT (6) 신청내용 확인 =================

                  견적은 **서버가 저장된 신청으로 계산한 값**(`quote`)을 그대로 그린다 — 화면 계산값이 아니다.
                  결제는 금액을 보내지 않는다(서버 계산). 실패·미등록은 '나중에 결제하기' 로 넘어갈 수 있다(명세의 bypass).
                */}
                {step === STEP.PAYMENT && (
                    <div className="flex flex-col h-full">
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">신청 내용을 확인해주세요</h2>
                        <p className="text-gray-500 mb-6 text-sm">오늘은 초기 세팅비와 스티커 비용만 결제합니다.</p>

                        {isQuoteLoading && !quoteView && (
                            <div className="flex items-center justify-center py-10 text-gray-400 gap-2 text-[14px]">
                                <Loader2 className="w-5 h-5 animate-spin" /> 결제 금액을 불러오는 중입니다.
                            </div>
                        )}
                        {quoteError && (
                            <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 p-4 text-[13px] text-red-700">
                                결제 금액을 불러오지 못했습니다. 새로고침 후 다시 시도해주세요.
                            </div>
                        )}
                        {quoteView && !quoteView.quote && !isQuoteLoading && (
                            <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-[13px] text-amber-800">
                                스티커 선택 내용이 없습니다. '뒤로' 를 눌러 비치 방법을 먼저 골라주세요.
                            </div>
                        )}

                        {quoteView?.quote && (
                            <div className="mb-5 rounded-2xl border border-gray-200 overflow-hidden" data-testid="quote">
                                <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200 flex items-center gap-2">
                                    <Receipt className="w-4 h-4 text-gray-500" />
                                    <span className="text-[12px] font-bold text-gray-600">오늘 결제 내역</span>
                                </div>
                                <dl className="p-4 text-[14px] text-gray-700 space-y-3">
                                    <div className="flex justify-between items-start gap-3">
                                        <dt>
                                            초기 세팅비
                                            {/* 프로모션은 정가표 INSTALL_PROMO 행의 유무로 켜진다 — 없으면 배지·취소선 없이 정가만(2026-10-14). */}
                                            {pricing?.setupFeePromotionLabel && (
                                                <span className="block text-[12px] text-rose-600 font-semibold mt-0.5" data-testid="promo-label">{pricing.setupFeePromotionLabel}</span>
                                            )}
                                        </dt>
                                        <dd className="text-right">
                                            {pricing?.setupFeePromotionLabel && quoteView.quote.setupFeeListNet !== quoteView.quote.setupFeeNet && (
                                                <span className="text-gray-400 line-through mr-2" data-testid="list-price">{won(quoteView.quote.setupFeeListNet)}</span>
                                            )}
                                            <span className="font-bold text-gray-900">{won(quoteView.quote.setupFeeNet)}</span>
                                        </dd>
                                    </div>
                                    <div className="flex justify-between items-start gap-3">
                                        <dt>
                                            스티커
                                            <span className="block text-[12px] text-gray-500 mt-0.5">
                                                {quoteView.quote.stickerQuantity > 0
                                                    ? `${describeStickerType(quoteView.stickerType)} ${quoteView.quote.stickerQuantity}장`
                                                    : '이미지만 받기 (무료)'}
                                            </span>
                                        </dt>
                                        <dd className="font-bold text-gray-900">{won(quoteView.quote.stickerNet)}</dd>
                                    </div>
                                    <div className="border-t border-dashed border-gray-200 pt-3 space-y-1.5 text-[13px] text-gray-500">
                                        <div className="flex justify-between"><dt>공급가 합계</dt><dd>{won(quoteView.quote.supplyNet)}</dd></div>
                                        <div className="flex justify-between"><dt>부가세 ({pricing?.vatRatePercent}%)</dt><dd>{won(quoteView.quote.vat)}</dd></div>
                                    </div>
                                    <div className="flex justify-between items-end border-t border-gray-200 pt-3">
                                        <dt className="font-bold text-gray-900">오늘 결제 금액</dt>
                                        <dd className="text-xl font-bold text-blue-600">{won(quoteView.quote.amount)}</dd>
                                    </div>
                                </dl>
                            </div>
                        )}

                        {pricing && (
                            <div className="mb-5 bg-[#FFF8E7] rounded-2xl p-4 border border-[#FDEBCA]">
                                <div className="flex items-center gap-1.5 mb-1.5">
                                    <Info className="w-4 h-4 text-[#F59E0B]" />
                                    <h4 className="font-bold text-[#D97706] text-sm">반드시 확인하세요</h4>
                                </div>
                                {/* 문구는 2026-10-12 사용자 확정본. 단가는 서버 상수표(`pricing`)에서 온다 — 숫자를 여기 박지 않는다. */}
                                <ul className="text-[13px] text-[#92400E] leading-relaxed pl-5 space-y-1" data-testid="ongoing-cost-notice">
                                    <li>• 서비스 이용료, 메시지 비용은 오늘 등록한 카드로 매월 별도 정산됩니다.</li>
                                    <li>• 서비스 이용료 : 매월 {won(pricing.monthlyFeeNet)}</li>
                                    <li>• 카카오 메시지 : 이벤트 혜택 발송({pricing.messageBenefitUnitPrice}원/건), 재방문 유도({pricing.messageRetargetUnitPrice}원/건)</li>
                                </ul>
                                <p className="mt-1.5 pl-5 text-[12px] text-[#B45309]">(VAT 별도)</p>
                            </div>
                        )}

                        {/* 결제 카드 */}
                        <div className="mb-5 rounded-2xl border border-gray-200 p-4 flex items-center gap-3">
                            <CreditCard className="w-5 h-5 text-gray-400 shrink-0" />
                            <div className="flex-1 text-[14px]">
                                {quoteView?.defaultCard ? (
                                    <>
                                        <span className="font-semibold text-gray-900">{quoteView.defaultCard.cardCompany ?? '등록된 카드'}</span>
                                        <span className="ml-2 text-gray-500">{quoteView.defaultCard.maskedNumber ?? ''}</span>
                                    </>
                                ) : (
                                    <span className="text-amber-700 font-semibold">등록된 결제 카드가 없습니다</span>
                                )}
                            </div>
                        </div>

                        {payState === 'failed' && payError && (
                            <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 p-4 flex items-start gap-2" data-testid="pay-error">
                                <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                                <div className="text-[13px] text-red-800 leading-relaxed">
                                    <div className="font-bold mb-0.5">결제에 실패했습니다</div>
                                    <div>{payError.message}</div>
                                    {payError.result && <div className="text-[11px] text-red-500 mt-1">오류 코드: {payError.result}</div>}
                                </div>
                            </div>
                        )}

                        {isPaid ? (
                            <>
                                <div className="mb-5 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-2" data-testid="pay-success">
                                    <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                                    <div className="text-[13px] text-emerald-800 leading-relaxed">
                                        <div className="font-bold mb-0.5">신청이 완료되었습니다</div>
                                        <div>
                                            결제가 끝났습니다
                                            {payResult?.amount != null && ` (${won(payResult.amount)})`}
                                            {quoteView?.payment?.paidAt && !payResult && ` · ${quoteView.payment.paidAt}`}.
                                        </div>
                                    </div>
                                </div>
                                <button onClick={() => goTo(STEP.BENEFIT_EXAMPLE)} className={`${primaryButton} mt-auto`}>다음으로</button>
                            </>
                        ) : (
                            <div className="mt-auto space-y-2.5">
                                {hasDefaultCard ? (
                                    <button
                                        onClick={handlePay}
                                        disabled={payState === 'paying' || !quoteView?.quote}
                                        className={primaryButton}
                                    >
                                        {payState === 'paying' ? '결제 중...' : `결제하기${quoteView?.quote ? ` · ${won(quoteView.quote.amount)}` : ''}`}
                                    </button>
                                ) : (
                                    <button onClick={startCardRegistration} className={primaryButton}>
                                        결제수단 등록하기
                                    </button>
                                )}
                                {payState === 'failed' && hasDefaultCard && (
                                    <button onClick={startCardRegistration} className={secondaryButton}>
                                        다른 카드 등록하기
                                    </button>
                                )}
                                <button onClick={handleSkipPayment} disabled={payState === 'paying'} className={secondaryButton}>
                                    {hasDefaultCard ? '나중에 결제하기' : '나중에 등록하기'}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* ================= BENEFIT_EXAMPLE (7) 혜택 예시 ================= */}
                {step === STEP.BENEFIT_EXAMPLE && (
                    <div className="flex flex-col h-full">
                        <div className="inline-block bg-indigo-50 text-indigo-600 px-3 py-1 rounded-full text-[13px] font-bold mb-3 w-max mt-1">
                            <Gift className="w-3.5 h-3.5 inline-block mr-1 -mt-0.5" />혜택은 담당자가 설정해 드려요
                        </div>
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">고객이 받게 될 혜택 예시</h2>
                        <p className="text-gray-500 mb-5 text-sm">통화로 확인한 혜택을 담당자가 별도 화면에서 입력해 이렇게 세팅됩니다.</p>
                        {/*
                          🟡 두 장 모두 **플레이스홀더(SVG)** — 담당자가 실제 캡처로 교체한다:
                             `assets/images/wizard/benefit_example_roulette.svg`, `benefit_example_setting.svg`
                        */}
                        <div className="grid grid-cols-2 gap-3 mb-6">
                            <figure className="m-0">
                                <img src={BenefitRoulette} alt="룰렛 화면 예시" className="w-full h-auto rounded-xl border border-gray-200" />
                                <figcaption className="mt-1.5 text-center text-[12px] text-gray-500">룰렛 화면</figcaption>
                            </figure>
                            <figure className="m-0">
                                <img src={BenefitSetting} alt="혜택 세팅 예시" className="w-full h-auto rounded-xl border border-gray-200" />
                                <figcaption className="mt-1.5 text-center text-[12px] text-gray-500">혜택 세팅 예시</figcaption>
                            </figure>
                        </div>
                        <button onClick={nextStep} className={`${primaryButton} mt-auto`}>다음으로</button>
                    </div>
                )}

                {/* ================= STORE (8) 객단가 + 로고 + 매장 컨셉 ================= */}
                {step === STEP.STORE && (
                    <div className="flex flex-col h-full">
                        <div className="inline-block bg-indigo-50 text-indigo-600 px-3 py-1 rounded-full text-[13px] font-bold mb-3 w-max mt-1">
                            마지막 단계입니다 👏
                        </div>
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">매장 정보를 입력해주세요</h2>
                        <p className="text-gray-500 mb-6 text-sm">객단가만 필수이고 나머지는 나중에 바꿀 수 있습니다.</p>

                        {/* 객단가 — 필수. 혜택 금액 추천·정산 계산의 기준값이라 비워둘 수 없다. */}
                        <div className="mb-6">
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                객단가 <span className="text-blue-600">(필수)</span>
                            </label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number"
                                    inputMode="numeric"
                                    min={0}
                                    value={unitPrice}
                                    onChange={(e) => setUnitPrice(e.target.value)}
                                    className="w-[160px] bg-gray-50 border border-gray-200 rounded-xl px-4 py-3.5 outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                                />
                                <span className="text-gray-700">원</span>
                            </div>
                            <p className="text-[13px] text-gray-500 mt-1.5 leading-relaxed">
                                고객 1인당 평균 결제 금액입니다.<br />
                                혜택 금액을 추천할 때 기준으로 씁니다.<br />
                                혜택 성과를 측정할 때 사용할 수 있습니다.
                            </p>
                        </div>

                        {/* 로고 — 선택 */}
                        <div className="mb-6">
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                로고 <span className="text-gray-400 font-normal">(선택)</span>
                            </label>
                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                accept="image/png, image/jpeg"
                                onChange={handleImageUpload}
                            />
                            <div
                                onClick={() => fileInputRef.current?.click()}
                                className="border-2 border-dashed border-gray-200 rounded-2xl bg-gray-50 h-40 flex flex-col items-center justify-center hover:bg-gray-100 hover:border-gray-300 transition-colors cursor-pointer group relative overflow-hidden"
                            >
                                {logoImage ? (
                                    <img src={logoImage} alt="Uploaded Logo" className="w-full h-full object-contain p-2" />
                                ) : (
                                    <>
                                        <div className="w-14 h-14 bg-white rounded-full shadow-sm flex items-center justify-center mb-3 group-hover:scale-105 transition-transform"><ImagePlus className="w-6 h-6 text-gray-400" /></div>
                                        <p className="text-sm font-medium text-gray-600">클릭하여 로고 이미지 업로드</p>
                                    </>
                                )}
                            </div>
                            <p className="text-[13px] text-indigo-700 mt-1.5">✨ AI 생성 시 해당 로고를 참고해 신뢰도있는 이미지가 나옵니다.</p>
                        </div>

                        {/* 매장 컨셉 — 선택 */}
                        <div className="mb-8">
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                                매장 컨셉 <span className="text-gray-400 font-normal">(선택)</span>
                            </label>
                            <input
                                type="text"
                                value={concept}
                                placeholder="밝은 분위기, 이국적"
                                maxLength={100}
                                onChange={(e) => setConcept(e.target.value)}
                                className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3.5 outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                            />
                            <p className="text-[13px] text-gray-500 mt-1.5">AI 생성시 입력한 내용을 반영할 수 있습니다.</p>
                        </div>

                        <button
                            onClick={handleSubmit}
                            // 객단가는 필수. 빈 값이나 0 이하가 저장되면 혜택 추천·정산 계산이 어긋난다.
                            disabled={!isUnitPriceValid || isSubmitting}
                            className={`${primaryButton} mt-auto`}
                        >
                            {isSubmitting ? '등록 중...' : '등록 완료'}
                        </button>
                    </div>
                )}

                {/* ================= DONE ================= */}
                {step === STEP.DONE && (
                    <div className="flex flex-col items-center justify-center h-full py-8 text-center pt-10">
                        <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-blue-500/30">
                            <Check className="w-10 h-10 text-white stroke-[3]" />
                        </div>
                        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 tracking-tight">신청이 완료되었어요!</h2>
                        <p className="text-primary mb-4 text-sm font-semibold">담당자가 순차적으로 카카오 채널 연동과 초기 세팅을 진행합니다.</p>
                        {isPaid ? (
                            <p className="text-gray-500 mb-10 text-sm">세팅이 끝나면 계정에 등록된 번호로 알림톡을 보내드립니다.</p>
                        ) : (
                            /* 미결제 — 사용자 규칙: 결제 전에는 세팅이 시작되지 않는다. 어디서 결제하는지도 함께 알린다. */
                            <div className="mb-10 w-full rounded-2xl border border-amber-200 bg-amber-50 p-4 text-left flex items-start gap-2" data-testid="done-unpaid">
                                <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                                <p className="text-[13px] text-amber-800 leading-relaxed">
                                    초기 세팅비가 아직 결제되지 않았습니다. 결제가 확인된 뒤 세팅이 시작됩니다.<br />
                                    서비스 목록 화면의 <strong>'설치비 결제하기'</strong> 로 언제든 결제할 수 있습니다.
                                </p>
                            </div>
                        )}
                        <button onClick={() => window.location.href = "/services"} className="w-full bg-gray-900 text-white rounded-xl py-4 font-semibold text-[16px] hover:bg-black transition-colors">서비스 목록으로</button>
                    </div>
                )}
            </div>

            {/* 이미지 크게 보기 팝업 (모달) */}
            {previewImage && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-4 animate-in fade-in duration-200"
                    onClick={() => setPreviewImage(null)}
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-2xl w-full flex flex-col animate-in zoom-in-95 duration-200"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center p-4 border-b">
                            <h3 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2">
                                <ImageIcon className="w-5 h-5 text-gray-500 shrink-0" />
                                {previewImage.name} 예시
                            </h3>
                            <button
                                onClick={() => setPreviewImage(null)}
                                className="p-1 hover:bg-gray-100 rounded-full transition-colors text-gray-500"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        {/* 모바일에서는 `min-h-[400px]` 이 헤더와 합쳐 화면을 넘겨 닫기 버튼까지 밀어냈다 */}
                        <div className="p-4 bg-gray-50 flex justify-center items-center h-[50vh] min-h-[220px] sm:min-h-[400px]">
                            <img
                                src={previewImage.imageUrl}
                                alt={previewImage.name}
                                className="max-w-full max-h-full object-contain rounded-lg shadow-sm"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * 수량 옵션 한 칸(라디오 카드). 세 옵션이 같은 모양이어야 하므로 한 컴포넌트로 그린다 —
 * 마크업을 복제하면 한쪽만 고치는 실수가 난다(이 저장소에서 반복돼 온 실패).
 */
function QuantityOption({name, value, selected, disabled, onSelect, title, price, lines = [], children}) {
    return (
        <label
            className={`flex items-start gap-3 p-4 rounded-2xl border cursor-pointer transition-all ${
                selected ? 'bg-blue-50 border-blue-500 ring-1 ring-blue-200' : 'bg-white border-gray-200 hover:bg-gray-50'
            } ${disabled ? 'opacity-70 cursor-not-allowed' : ''}`}
        >
            <input
                type="radio"
                name={name}
                value={value}
                checked={selected}
                disabled={disabled}
                onChange={() => onSelect(value)}
                className="w-4 h-4 mt-0.5 shrink-0 text-blue-600 border-gray-300 focus:ring-blue-500 cursor-pointer"
            />
            <span className="flex flex-col flex-1">
                <span className="flex items-baseline justify-between gap-3">
                    <span className={`text-sm font-semibold ${selected ? 'text-blue-700' : 'text-gray-700'}`}>{title}</span>
                    {price && <span className={`text-[13px] font-bold ${selected ? 'text-blue-700' : 'text-gray-600'}`}>{price}</span>}
                </span>
                {lines.length > 0 && (
                    <span className="text-[13px] text-gray-500 mt-1 leading-relaxed">
                        {lines.map((line) => (
                            <React.Fragment key={line}>· {line}<br /></React.Fragment>
                        ))}
                    </span>
                )}
                {children}
            </span>
        </label>
    );
}
